/**
 * Breadcrumbs — barra global de ruta de navegación.
 *
 * - Botones ← / → para moverse por el historial de la propia aplicación.
 * - Migas de pan jerárquicas (Inicio › Grupo › Sección › Registro), todas
 *   clicables excepto la página actual.
 *
 * El historial se lleva en una pila propia porque React Router no expone si
 * hay páginas hacia delante; así los botones se pueden deshabilitar de forma
 * coherente.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { useDetailCrumbLabel } from '../contexts/BreadcrumbContext';
import { buildBreadcrumbTrail } from '../utils/breadcrumbTrail';
import { useTranslation } from 'react-i18next';
import './Breadcrumbs.css';

const HISTORY_LIMIT = 80;
const currentEntry = (location) => `${location.pathname}${location.search}`;

export default function Breadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const detailLabel = useDetailCrumbLabel();
  const { t } = useTranslation();

  // Pila de rutas visitadas + posición actual.
  const entriesRef = useRef([]);
  const indexRef = useRef(-1);
  const pendingRef = useRef(null); // 'back' | 'forward' cuando lo mueve esta barra
  const [position, setPosition] = useState({ index: -1, length: 0 });

  useEffect(() => {
    const here = currentEntry(location);
    const entries = entriesRef.current;
    let index = indexRef.current;

    if (pendingRef.current === 'back' || pendingRef.current === 'forward') {
      // El usuario pulsó nuestros botones: la posición ya se movió.
      const direction = pendingRef.current === 'back' ? -1 : 1;
      pendingRef.current = null;
      if (entries[index + direction] === here) {
        index += direction;
      } else {
        // Si la entrada no coincide (por ejemplo tras un replace), se busca
        // la ocurrencia más cercana para no perder la referencia.
        const found = entries.lastIndexOf(here);
        index = found >= 0 ? found : Math.min(Math.max(index + direction, 0), entries.length - 1);
      }
    } else if (navigationType === 'POP') {
      // Atrás/adelante del navegador: se busca la ocurrencia más cercana de la
      // ruta en la pila propia para que «Adelante» siga siendo coherente.
      let closest = -1;
      entries.forEach((entry, entryIndex) => {
        if (entry !== here) return;
        if (closest < 0 || Math.abs(entryIndex - index) < Math.abs(closest - index)) closest = entryIndex;
      });
      if (closest >= 0) {
        index = closest;
      } else {
        entries.splice(index + 1);
        entries.push(here);
        if (entries.length > HISTORY_LIMIT) entries.shift();
        index = entries.length - 1;
      }
    } else if (entries[index] !== here) {
      // Navegación normal: se corta el futuro y se añade la página actual.
      entries.splice(index + 1);
      entries.push(here);
      if (entries.length > HISTORY_LIMIT) entries.shift();
      index = entries.length - 1;
    }

    indexRef.current = index;
    setPosition({ index, length: entries.length });
  }, [location, navigationType]);

  const goBack = useCallback(() => {
    if (indexRef.current <= 0) return;
    pendingRef.current = 'back';
    navigate(-1);
  }, [navigate]);

  const goForward = useCallback(() => {
    if (indexRef.current >= entriesRef.current.length - 1) return;
    pendingRef.current = 'forward';
    navigate(1);
  }, [navigate]);

  const trail = buildBreadcrumbTrail(location.pathname, detailLabel, t);
  const canGoBack = position.index > 0;
  const canGoForward = position.index >= 0 && position.index < position.length - 1;

  return (
    <div className="crumb-bar">
      <button
        type="button"
        className="crumb-nav-btn"
        onClick={goBack}
        disabled={!canGoBack}
        title="Atrás"
        aria-label="Atrás"
      >
        <ArrowLeftIcon />
      </button>
      <button
        type="button"
        className="crumb-nav-btn"
        onClick={goForward}
        disabled={!canGoForward}
        title="Adelante"
        aria-label="Adelante"
      >
        <ArrowRightIcon />
      </button>

      <div className="crumb-divider" aria-hidden="true" />

      <nav className="crumb-trail" aria-label="Ruta de navegación">
        {trail.map((crumb, index) => {
          const isLast = index === trail.length - 1;
          return (
            <React.Fragment key={`${crumb.label}-${index}`}>
              {index > 0 && <ChevronRightIcon className="crumb-sep" aria-hidden="true" />}
              {!isLast && crumb.to ? (
                <Link className="crumb-link" to={crumb.to} title={crumb.label}>
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={isLast ? 'crumb-current' : 'crumb-muted'}
                  aria-current={isLast ? 'page' : undefined}
                  title={crumb.label}
                >
                  {crumb.label}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    </div>
  );
}
